import { isDateInRange, todayLocalISO } from '@/lib/date-utils'
import type { DateRange, SelectionMode } from '@/types/teacher-request'

interface CalendarDayData {
  date: string
  is_school_day: boolean
  lessons: any[]
}

interface CalendarViewProps {
  month: number
  year: number
  days: CalendarDayData[]
  selectedDate: string
  onDateSelect: (date: string) => void
  onPrevMonth: () => void
  onNextMonth: () => void
  onToday: () => void

  // Time Picker v2
  selectionMode: SelectionMode
  dateRange: DateRange | null

  // Weekend config
  weekendSaturdayEnabled?: boolean
  weekendSundayEnabled?: boolean
}

const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
]

const DAY_NAMES = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

export function CalendarView({
  month,
  year,
  days,
  selectedDate,
  onDateSelect,
  onPrevMonth,
  onNextMonth,
  onToday,
  selectionMode,
  dateRange,
  weekendSaturdayEnabled = false,
  weekendSundayEnabled = false,
}: CalendarViewProps) {
  const today = todayLocalISO()

  const dayMap = new Map<string, CalendarDayData>()
  for (const d of days) {
    dayMap.set(d.date, d)
  }

  const showWeekend = weekendSaturdayEnabled || weekendSundayEnabled
  const visibleDayNames = showWeekend ? DAY_NAMES : DAY_NAMES.slice(0, 5)
  const daysPerWeek = showWeekend ? 7 : 5

  const lastDay = new Date(year, month, 0)

  const weeks: (string | null)[][] = []
  let currentWeek: (string | null)[] = []

  for (let day = 1; day <= lastDay.getDate(); day++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    const weekday = new Date(dateStr + 'T00:00:00').getDay()

    const isSaturday = weekday === 6
    const isSunday = weekday === 0

    if (!showWeekend) {
      if (isSaturday || isSunday) continue
    } else {
      if (isSaturday && !weekendSaturdayEnabled) continue
      if (isSunday && !weekendSundayEnabled) continue
    }

    if (currentWeek.length === 0 && weeks.length === 0) {
      const mondayBasedWeekday = (weekday + 6) % 7
      const adjustedWeekday = showWeekend ? mondayBasedWeekday : Math.min(mondayBasedWeekday, 4)
      currentWeek = Array(adjustedWeekday).fill(null)
    }

    currentWeek.push(dateStr)
    if (currentWeek.length === daysPerWeek) {
      weeks.push(currentWeek)
      currentWeek = []
    }
  }

  if (currentWeek.length > 0) {
    while (currentWeek.length < daysPerWeek) {
      currentWeek.push(null)
    }
    weeks.push(currentWeek)
  }

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onPrevMonth} className="px-3 py-1 text-sm border rounded hover:bg-gray-50" aria-label="Vorheriger Monat">
          ←
        </button>
        <h2 className="text-lg font-semibold">
          {MONTH_NAMES[month - 1]} {year}
        </h2>
        <div className="flex gap-2">
          <button onClick={onToday} className="px-3 py-1 text-sm border rounded hover:bg-gray-50">
            Heute
          </button>
          <button onClick={onNextMonth} className="px-3 py-1 text-sm border rounded hover:bg-gray-50" aria-label="Nächster Monat">
            →
          </button>
        </div>
      </div>

      <div className={`grid ${showWeekend ? 'grid-cols-7' : 'grid-cols-5'} gap-1 mb-2`}>
        {visibleDayNames.map((d) => (
          <div key={d} className="text-center text-sm font-medium text-gray-500 py-2">
            {d}
          </div>
        ))}
      </div>

      <div className={`grid ${showWeekend ? 'grid-cols-7' : 'grid-cols-5'} gap-1`}>
        {weeks.map((week, wi) =>
          week.map((dateStr, di) => {
            if (!dateStr) {
              return <div key={`empty-${wi}-${di}`} className="h-12" />
            }

            const dayData = dayMap.get(dateStr)
            const hasLessons = dayData && dayData.lessons.length > 0
            const hasRequestable = dayData?.lessons.some((l) => l.is_requestable)
            const isSelected = dateStr === selectedDate
            const isToday = dateStr === today
            const isSchoolDay = dayData?.is_school_day ?? true
            const isPast = dateStr < (today || '')

            // Range selection styling
            const isFrom = dateRange?.from === dateStr
            const isTo = dateRange?.to === dateStr
            const isInRange = selectionMode === 'range' && dateRange && isDateInRange(dateStr, dateRange.from, dateRange.to)

            let bgClass = 'hover:bg-gray-50'
            if (isPast) bgClass = 'bg-gray-100'
            else if (selectionMode === 'range' && isInRange) {
              if (isFrom) bgClass = 'bg-green-50 border-green-500 border-2'
              else if (isTo) bgClass = 'bg-red-50 border-red-500 border-2'
              else bgClass = 'bg-blue-50 border-blue-200'
            } else if (isSelected) bgClass = 'bg-blue-100 border-blue-300'
            else if (isToday) bgClass = 'bg-yellow-50 border-yellow-300'
            else if (!isSchoolDay) bgClass = 'bg-gray-100'
            else if (hasRequestable) bgClass = 'bg-green-50 hover:bg-green-100'
            else if (hasLessons) bgClass = 'bg-blue-50 hover:bg-blue-100'

            const weekday = new Date(dateStr + 'T00:00:00').getDay()
            const isWeekend = weekday === 0 || weekday === 6

            return (
              <button
                key={dateStr}
                onClick={() => isSchoolDay && !isPast && onDateSelect(dateStr)}
                disabled={!isSchoolDay || isPast}
                className={`h-12 rounded border text-sm transition-colors relative ${bgClass} ${
                  !isSchoolDay || isPast ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                }`}
                aria-label={`${dateStr}${isFrom ? ', Von-Datum' : ''}${isTo ? ', Bis-Datum' : ''}${isInRange && !isFrom && !isTo ? ', im gewählten Zeitraum' : ''}`}
                aria-pressed={isInRange || isSelected}
              >
                <span className={isToday ? 'font-bold text-blue-600' : ''}>
                  {parseInt(dateStr.split('-')[2] || '0', 10)}
                </span>
                {hasLessons && (
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mx-auto mt-0.5" />
                )}
                {isWeekend && !isSchoolDay && (
                  <div className="text-[8px] text-gray-400 leading-none">
                    {weekday === 0 ? 'So' : 'Sa'}
                  </div>
                )}
                {isFrom && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full" />
                )}
                {isTo && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full" />
                )}
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}
